/**
 * OperationsHistory — calm chronological history of the user's
 * recent operations.
 *
 * Operator brief (May 14, 2026 — Operational Continuity Phase D):
 *   "Give users one canonical place to answer: 'What happened to my
 *    upload? Did my review finish? Did that recovery succeed?'
 *    Calm operational tone, no analytics-dashboard feel, no clutter,
 *    mobile-first, easy chronological understanding."
 *
 * Reads from `GET /api/operations/history`. Read-only. Items are
 * pre-projected by the backend into a unified shape so the page
 * itself stays simple: list → group by day → render.
 *
 * Tone (operator-locked):
 *   • No big counts banner.
 *   • No charts.
 *   • Day groups feel like a journal, not a dashboard.
 *   • Status badges are descriptive, not punitive.
 *   • Deep-links are optional, not mandatory.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Upload, Brain, FileText, Sparkles, GraduationCap,
  Check, X, AlertCircle, Loader2, ChevronRight,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const KIND_ICON = {
  upload:          Upload,
  ai_review:       Brain,
  review_request:  FileText,
  ai_offer:        Sparkles,
  work_lesson:     GraduationCap,
};

// Outcome → calm visual tone. Never alarming. "failed" is rose but
// muted; "dismissed" is slate (neutral, not negative).
const OUTCOME_TONE = {
  completed: {
    text:  "text-emerald-700 dark:text-emerald-300",
    bg:    "bg-emerald-50 dark:bg-emerald-900/30",
    ring:  "ring-emerald-200 dark:ring-emerald-800",
    Icon:  Check,
    label: "Completed",
  },
  in_progress: {
    text:  "text-sky-700 dark:text-sky-300",
    bg:    "bg-sky-50 dark:bg-sky-900/30",
    ring:  "ring-sky-200 dark:ring-sky-800",
    Icon:  Loader2,
    label: "In progress",
  },
  dismissed: {
    text:  "text-slate-600 dark:text-slate-400",
    bg:    "bg-slate-100 dark:bg-slate-800/60",
    ring:  "ring-slate-200 dark:ring-slate-700",
    Icon:  X,
    label: "Dismissed",
  },
  failed: {
    text:  "text-rose-700 dark:text-rose-300",
    bg:    "bg-rose-50 dark:bg-rose-900/30",
    ring:  "ring-rose-200 dark:ring-rose-800",
    Icon:  AlertCircle,
    label: "Recovered" /* failed-but-acknowledged is calmer than "failed" */,
  },
};

const FILTERS = [
  { key: "all",       label: "All",        match: () => true },
  { key: "uploads",   label: "Uploads",    match: (it) => it.kind === "upload" },
  { key: "reviews",   label: "Reviews",    match: (it) => it.kind === "ai_review" || it.kind === "review_request" },
  { key: "lessons",   label: "Lessons",    match: (it) => it.kind === "work_lesson" || it.kind === "ai_offer" },
];

import { fmtDate as _londonFmt, londonYMD as _londonYMD } from "../utils/timezone";

// GC64 · MP-10 (Feb 16, 2026) — timezone-aware bucketing.
//
// Backend now emits ISO strings with `+00:00` UTC offset (Motor
// `tz_aware=True`). We bucket by DAY BOUNDARIES IN EUROPE/LONDON
// so a 23:30 upload on a UK evening never lands in "yesterday"
// just because its underlying UTC hour crossed midnight. See
// utils/timezone.js for the shared helper.

// Day-group label for an ISO timestamp. Calm relative wording where
// it helps, exact date where it doesn't (the journal feel).
function dayBucket(iso) {
  if (!iso) return "Earlier";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Earlier";
  const now = new Date();
  const todayYMD = _londonYMD(now);
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const yesterdayYMD = _londonYMD(yesterday);
  const weekAgo = new Date(now); weekAgo.setDate(now.getDate() - 7);
  const weekAgoYMD = _londonYMD(weekAgo);
  const rowYMD = _londonYMD(d);
  if (rowYMD === todayYMD) return "Today";
  if (rowYMD === yesterdayYMD) return "Yesterday";
  if (rowYMD >= weekAgoYMD) return "Earlier this week";
  // Otherwise group by month so the list stays readable without
  // turning into a wall of identical date headers.
  return d.toLocaleDateString("en-GB", {
    timeZone: "Europe/London",
    month: "long", year: "numeric",
  });
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const day = Math.round(h / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString("en-GB", {
    timeZone: "Europe/London",
    month: "short", day: "numeric",
  });
}

export default function OperationsHistory() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/operations/history`, {
          withCredentials: true,
        });
        if (!cancelled) {
          setData(r.data || { items: [], counts: {}, window_days: 30 });
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || "Could not load history.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const items = data?.items || [];
  const filteredItems = useMemo(() => {
    const def = FILTERS.find((f) => f.key === filter) || FILTERS[0];
    return items.filter(def.match);
  }, [items, filter]);

  // Group filtered items by day bucket while preserving the
  // server-supplied chronological order (already DESC).
  const grouped = useMemo(() => {
    const buckets = [];
    const byKey = new Map();
    for (const it of filteredItems) {
      const key = dayBucket(it.updated_at || it.started_at);
      if (!byKey.has(key)) {
        const entry = { key, items: [] };
        byKey.set(key, entry);
        buckets.push(entry);
      }
      byKey.get(key).items.push(it);
    }
    return buckets;
  }, [filteredItems]);

  const counts = data?.counts || {};
  const totalShown = filteredItems.length;

  return (
    <div
      data-testid="operations-history-page"
      className="min-h-[100dvh] bg-slate-50 dark:bg-slate-950"
    >
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            data-testid="operations-history-back"
            onClick={() => nav(-1)}
            aria-label="Back"
            className="p-1.5 -ml-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1
              data-testid="operations-history-title"
              className="text-base font-extrabold text-slate-900 dark:text-white leading-tight truncate"
            >
              Recent activity
            </h1>
            <p
              data-testid="operations-history-subtitle"
              className="text-[11px] text-slate-500 dark:text-slate-400 leading-tight"
            >
              {data?.window_days
                ? `Last ${data.window_days} days · ${items.length} ${items.length === 1 ? "item" : "items"}`
                : "Your recent operations"}
            </p>
          </div>
        </div>

        {/* Quiet counts strip — never a dashboard. One calm line. */}
        {items.length > 0 && (
          <div
            data-testid="operations-history-counts"
            className="max-w-lg mx-auto px-4 pb-2 text-[11px] text-slate-500 dark:text-slate-400 leading-tight"
          >
            {counts.completed || 0} completed · {counts.in_progress || 0} in progress
            {counts.dismissed ? ` · ${counts.dismissed} dismissed` : ""}
            {counts.failed    ? ` · ${counts.failed} recovered` : ""}
          </div>
        )}

        {/* Filter chips */}
        {items.length > 0 && (
          <div className="max-w-lg mx-auto px-4 pb-3 flex gap-1.5 overflow-x-auto">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                data-testid={`operations-history-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`text-[12px] font-bold px-3 py-1 rounded-full whitespace-nowrap transition ${
                  filter === f.key
                    ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                    : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* Body */}
      <main className="max-w-lg mx-auto px-4 py-4 pb-20">
        {loading && (
          <div
            data-testid="operations-history-loading"
            className="flex items-center justify-center py-12 text-slate-400"
          >
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}

        {!loading && error && (
          <div
            data-testid="operations-history-error"
            className="rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 p-4 text-[13px] text-rose-700 dark:text-rose-300"
          >
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div
            data-testid="operations-history-empty"
            className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 text-center"
          >
            <p className="text-sm font-extrabold text-slate-700 dark:text-slate-200">
              No recent activity yet.
            </p>
            <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-1.5 leading-snug">
              Uploads, reviews and lessons will appear here as you
              use the platform — so you can always see what
              happened.
            </p>
          </div>
        )}

        {!loading && !error && items.length > 0 && totalShown === 0 && (
          <div
            data-testid="operations-history-filter-empty"
            className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 text-center text-[12px] text-slate-500 dark:text-slate-400"
          >
            Nothing in this view. Try a different filter.
          </div>
        )}

        {!loading && !error && grouped.map((bucket) => (
          <section
            key={bucket.key}
            data-testid={`operations-history-bucket-${bucket.key.replace(/\s+/g, "-").toLowerCase()}`}
            className="mb-5"
          >
            <h2 className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500 dark:text-slate-400 mb-2 px-1">
              {bucket.key}
            </h2>
            <ul className="space-y-1.5">
              {bucket.items.map((it) => (
                <HistoryItem key={`${it.kind}-${it.id}`} item={it} onOpen={() => it.deep_link && nav(it.deep_link)} />
              ))}
            </ul>
          </section>
        ))}
      </main>
    </div>
  );
}

function HistoryItem({ item, onOpen }) {
  const Icon = KIND_ICON[item.kind] || Loader2;
  const tone = OUTCOME_TONE[item.outcome] || OUTCOME_TONE.in_progress;
  const StatusIcon = tone.Icon;
  const isInProgress = item.outcome === "in_progress";
  const isFailed = item.outcome === "failed";

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        disabled={!item.deep_link}
        data-testid={`operations-history-item-${item.kind}-${item.id}`}
        data-kind={item.kind}
        data-outcome={item.outcome}
        className={`w-full text-left rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 flex items-center gap-3 ${item.deep_link ? "hover:border-slate-300 dark:hover:border-slate-700 transition" : ""}`}
      >
        <div className={`w-9 h-9 rounded-full ${tone.bg} ${tone.ring} ring-1 flex items-center justify-center flex-none`}>
          <Icon className={`w-4 h-4 ${tone.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-extrabold text-slate-900 dark:text-white leading-tight truncate">
            {item.label}
          </p>
          {item.detail ? (
            <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-tight truncate mt-0.5">
              {item.detail}
            </p>
          ) : null}
          <div className="mt-1 flex items-center gap-2 text-[10px]">
            <span
              data-testid={`operations-history-status-${item.outcome}`}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-bold ${tone.text} ${tone.bg}`}
            >
              <StatusIcon className={`w-2.5 h-2.5 ${isInProgress ? "animate-spin" : ""}`} />
              {/* Calm wording per outcome — already encoded on tone.label */}
              {isFailed ? "Needs attention" : tone.label}
            </span>
            <span className="text-slate-400 dark:text-slate-500">
              {timeAgo(item.updated_at || item.started_at)}
            </span>
          </div>
        </div>
        {item.deep_link ? (
          <ChevronRight className="w-4 h-4 text-slate-300 dark:text-slate-600 flex-none" />
        ) : null}
      </button>
    </li>
  );
}
